import { Controller } from "@hotwired/stimulus"

/**
 * Admin DataTable Controller - Client-side sorting, filtering, and selection
 *
 * Features:
 * - Column sorting (asc/desc)
 * - Search/filter with debounce
 * - Row selection (single/multiple)
 * - Pagination integration (server-side via Pagy)
 * - Empty state handling
 *
 * Usage:
 * <div data-controller="admin-datatable">
 *   <input data-admin-datatable-target="search" data-action="input->admin-datatable#filter">
 *   <table data-admin-datatable-target="table">...</table>
 * </div>
 */
export default class extends Controller {
  static targets = ["table", "search", "row", "selectAll", "selectedCount", "bulkActions"]
  static values = {
    sortColumn: { type: Number, default: -1 },
    sortDirection: { type: String, default: "asc" }
  }

  connect() {
    this.selectedRows = new Set()
    this.updateSelectedCount()
  }

  // Sort table by column
  sort(event) {
    const th = event.currentTarget
    const columnIndex = Array.from(th.parentElement.children).indexOf(th)

    // Toggle sort direction if same column
    if (this.sortColumnValue === columnIndex) {
      this.sortDirectionValue = this.sortDirectionValue === "asc" ? "desc" : "asc"
    } else {
      this.sortColumnValue = columnIndex
      this.sortDirectionValue = "asc"
    }

    // Update sort indicators
    this.updateSortIndicators(th)

    // Perform sort
    this.performSort(columnIndex, this.sortDirectionValue)
  }

  // Filter/search table rows
  filter(event) {
    // Debounce search
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => {
      const query = event.target.value.toLowerCase().trim()

      this.rowTargets.forEach(row => {
        const text = row.textContent.toLowerCase()
        const matches = text.includes(query)

        if (matches) {
          row.classList.remove("hidden")
        } else {
          row.classList.add("hidden")
        }
      })

      // Update empty state
      this.updateEmptyState()
    }, 300)
  }

  // Toggle select all checkbox
  toggleSelectAll(event) {
    const checked = event.target.checked

    this.rowTargets.forEach(row => {
      if (!row.classList.contains("hidden")) {
        const checkbox = row.querySelector('input[type="checkbox"]')
        if (checkbox) {
          checkbox.checked = checked
          this.updateRowSelection(row, checked)
        }
      }
    })

    this.updateSelectedCount()
    this.updateBulkActions()
  }

  // Toggle single row selection
  toggleRow(event) {
    const checkbox = event.currentTarget
    const row = checkbox.closest("tr")
    const checked = checkbox.checked

    this.updateRowSelection(row, checked)
    this.updateSelectedCount()
    this.updateSelectAllState()
    this.updateBulkActions()
  }

  // Perform client-side sort
  performSort(columnIndex, direction) {
    const tbody = this.tableTarget.querySelector("tbody")
    const rows = Array.from(this.rowTargets)

    rows.sort((a, b) => {
      const aCell = a.children[columnIndex]
      const bCell = b.children[columnIndex]

      if (!aCell || !bCell) return 0

      // Get sortable value (data attribute or text content)
      const aValue = aCell.dataset.sortValue || aCell.textContent.trim()
      const bValue = bCell.dataset.sortValue || bCell.textContent.trim()

      // Try numeric comparison first
      const aNum = parseFloat(aValue)
      const bNum = parseFloat(bValue)

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return direction === "asc" ? aNum - bNum : bNum - aNum
      }

      // Fallback to string comparison
      return direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    })

    // Re-append sorted rows
    rows.forEach(row => tbody.appendChild(row))
  }

  // Update sort indicators (arrows)
  updateSortIndicators(activeTh) {
    // Remove all sort indicators
    this.tableTarget.querySelectorAll("th").forEach(th => {
      th.classList.remove("sort-asc", "sort-desc")
      const icon = th.querySelector('[data-admin-datatable-target="sortIcon"]')
      if (icon) icon.remove()
    })

    // Add sort indicator to active column
    const icon = document.createElement("span")
    icon.dataset.adminDatatableTarget = "sortIcon"
    icon.classList.add("ml-1", "inline-block")
    icon.innerHTML = this.sortDirectionValue === "asc" ? "↑" : "↓"
    activeTh.appendChild(icon)
  }

  // Update row selection state
  updateRowSelection(row, selected) {
    const rowId = row.dataset.rowId

    if (selected) {
      this.selectedRows.add(rowId)
      row.classList.add("bg-primary-50")
    } else {
      this.selectedRows.delete(rowId)
      row.classList.remove("bg-primary-50")
    }
  }

  // Update selected count display
  updateSelectedCount() {
    if (this.hasSelectedCountTarget) {
      const count = this.selectedRows.size
      this.selectedCountTarget.textContent = count
    }
  }

  // Update select all checkbox state
  updateSelectAllState() {
    if (this.hasSelectAllTarget) {
      const visibleRows = this.rowTargets.filter(row => !row.classList.contains("hidden"))
      const visibleChecked = visibleRows.filter(row => {
        const checkbox = row.querySelector('input[type="checkbox"]')
        return checkbox && checkbox.checked
      })

      this.selectAllTarget.checked = visibleRows.length > 0 && visibleChecked.length === visibleRows.length
      this.selectAllTarget.indeterminate = visibleChecked.length > 0 && visibleChecked.length < visibleRows.length
    }
  }

  // Show/hide bulk actions
  updateBulkActions() {
    if (this.hasBulkActionsTarget) {
      if (this.selectedRows.size > 0) {
        this.bulkActionsTarget.classList.remove("hidden")
      } else {
        this.bulkActionsTarget.classList.add("hidden")
      }
    }
  }

  // Update empty state visibility
  updateEmptyState() {
    const visibleRows = this.rowTargets.filter(row => !row.classList.contains("hidden"))
    const emptyState = this.element.querySelector('[data-admin-datatable-target="emptyState"]')

    if (emptyState) {
      if (visibleRows.length === 0) {
        emptyState.classList.remove("hidden")
        this.tableTarget.classList.add("hidden")
      } else {
        emptyState.classList.add("hidden")
        this.tableTarget.classList.remove("hidden")
      }
    }
  }

  // Get selected row IDs
  getSelectedIds() {
    return Array.from(this.selectedRows)
  }
}
